import { FC, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Slider, { Settings as SliderSettings } from 'react-slick';

import { isEmpty } from '@telialt/utils/src/helpers';

import { BREAKPOINT_MEDIUM, THUMBNAILS_PER_ROW } from './constants';
import {
    AccessibilityDiv,
    ButtonAccessibilityDiv,
    GalleryItem,
    GalleryItemImg,
    GalleryThumbnail,
    GalleryThumbnailImg,
    GalleryThumbnailsInRows,
    GalleryWrapper,
    ShowMoreThumbnailsButton,
} from './Gallery.styles';
import { useArrowKeys, useWindowWidth } from './hooks';
import { NavArrowButton } from './NavArrowButton';
import { GALLERY_TEST_ID } from './test-ids';
import type { GalleryImage } from './types';
import { useZoomControls } from './ZoomProvider';
import { SpotlightCursor } from './ZoomProvider/SpotlightCursor';

type GalleryProps = {
    images: GalleryImage[];
    thumbs?: GalleryImage[];
    selectedId: number;
    scrollThumbs?: boolean;
    isZoomEnabled?: boolean;
    isFloatingNavButtonsEnabled?: boolean;
    onThumbnailSelect?: (id: number) => void;
    onZoomImage?: () => void;
    renderMarketingTag?: () => ReactNode;
    isModalOpen?: boolean;
};

export const Gallery: FC<GalleryProps> = ({
    images,
    thumbs,
    scrollThumbs = false,
    selectedId: selectedIdProp,
    isZoomEnabled = false,
    isFloatingNavButtonsEnabled,
    onThumbnailSelect,
    onZoomImage,
    renderMarketingTag,
    isModalOpen,
}) => {
    const [selectedId, setSelectedId] = useState<number>(selectedIdProp);
    const sliderMain = useRef<Slider>(null);
    const sliderThumbs = useRef<Slider>(null);
    const windowWidth = useWindowWidth();

    const [hasShowMoreThumbnails, setHasShowMoreThumbnails] = useState(true);
    const [isThumbnailFocused, setIsThumbnailFocused] = useState(false);

    const thumbsToScroll = windowWidth <= BREAKPOINT_MEDIUM ? THUMBNAILS_PER_ROW.MOBILE : THUMBNAILS_PER_ROW.DESKTOP;
    const thumbnails = thumbs || images;
    const mobileImages =
        windowWidth <= BREAKPOINT_MEDIUM && hasShowMoreThumbnails && images.length > THUMBNAILS_PER_ROW.MOBILE
            ? images.slice(0, THUMBNAILS_PER_ROW.MOBILE_SHOW_MORE)
            : images;
    const mobileThumbnails =
        windowWidth <= BREAKPOINT_MEDIUM && hasShowMoreThumbnails && thumbnails.length > THUMBNAILS_PER_ROW.MOBILE
            ? thumbnails.slice(0, THUMBNAILS_PER_ROW.MOBILE_SHOW_MORE)
            : thumbnails;
    const numberOfThumbnailsHidden = thumbnails.length - mobileThumbnails.length;
    const { updateCoordinates, enableHoveringState, disableHoveringState, isCursorHovering } = useZoomControls();

    const handleImageZoom = () => {
        disableHoveringState();
        setHasShowMoreThumbnails(false);
        onZoomImage?.();
    };

    const goToThumb = useCallback(
        (index: number) => {
            if (scrollThumbs) {
                const thumbsPage = Math.floor(index / thumbsToScroll);
                const thumbsSlide = thumbsPage * thumbsToScroll;

                sliderThumbs.current?.slickGoTo(thumbsSlide);
            } else {
                sliderThumbs.current?.slickGoTo(index);
            }
        },
        [scrollThumbs, thumbsToScroll],
    );

    const prevImage = () => sliderMain?.current?.slickPrev();
    const nextImage = () => sliderMain?.current?.slickNext();

    const onArrowRight = () => {
        sliderMain?.current?.slickNext();
        document
            .getElementById(mobileThumbnails[mobileThumbnails.length - 1 === selectedId ? 0 : selectedId + 1].src)
            ?.focus();
    };

    const onArrowLeft = () => {
        sliderMain?.current?.slickPrev();
        document
            .getElementById(mobileThumbnails[selectedId === 0 ? mobileThumbnails.length - 1 : selectedId - 1].src)
            ?.focus();
    };

    useArrowKeys(onArrowRight, onArrowLeft, isThumbnailFocused);

    const goToImage = (index: number) => {
        return sliderMain.current?.slickGoTo(index);
    };

    useEffect(() => {
        if (selectedId !== selectedIdProp) {
            setSelectedId(selectedIdProp);
            goToThumb(selectedIdProp);
            goToImage(selectedIdProp);
        }
    }, [goToThumb, selectedId, selectedIdProp]);

    if (isEmpty(images)) {
        return null;
    }

    const onShowMoreThumbnailsClick = () => {
        setHasShowMoreThumbnails(false);
    };

    const handleTabEnterPress = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && index !== selectedId) {
            e.preventDefault();
            setSelectedId(index);
            goToImage(index);
            onThumbnailSelect?.(index);
        } else if ((e.key === 'Enter' || e.key === ' ') && index === selectedId) {
            onZoomImage?.();
        }
    };

    const handleThumbnailClick = (index: number) => {
        setSelectedId(index);
        goToImage(index);
        onThumbnailSelect?.(index);
    };

    const handleGallerySwipe = (index: number) => {
        setSelectedId(index);
        goToThumb(index);
        onThumbnailSelect?.(index);
    };

    const sliderGetRowCount = (thumbsToShow: number) => {
        const count = images.length;
        const rows = thumbsToShow > 0 && Math.ceil(count / thumbsToShow + 1);

        return rows || undefined;
    };

    const settingsMain: SliderSettings = {
        arrows: false,
        dots: false,
        fade: false,
        initialSlide: selectedIdProp ?? undefined,
        infinite: mobileImages?.length > 1,
        slidesToShow: 1,
        slidesToScroll: 1,
        lazyLoad: 'ondemand',
        afterChange: handleGallerySwipe,
    };

    const settingsThumbs: SliderSettings = {
        rows: sliderGetRowCount(thumbsToScroll),
        arrows: false,
        dots: false,
        infinite: false,
        slidesPerRow: thumbsToScroll,
        lazyLoad: 'ondemand',
        swipeToSlide: true,
        focusOnSelect: false,
    };

    const floatingNavButtons = (
        <>
            <NavArrowButton direction="backward" dataTest={GALLERY_TEST_ID.LIGHTBOX_PREV_BTN} onClick={prevImage} />
            <NavArrowButton direction="forward" dataTest={GALLERY_TEST_ID.LIGHTBOX_NEXT_BTN} onClick={nextImage} />
        </>
    );

    const galleriesCarousel = (
        <Slider ref={sliderMain} {...settingsMain}>
            {mobileImages?.map((item) => (
                <GalleryItem tabIndex={0} key={item.src} onClick={handleImageZoom} role="button">
                    {isZoomEnabled ? (
                        <GalleryItemImg
                            src={item.src}
                            alt={item.altText}
                            onMouseMove={updateCoordinates}
                            onMouseEnter={enableHoveringState}
                            onMouseLeave={disableHoveringState}
                            $isBlurred={isCursorHovering}
                        />
                    ) : (
                        <GalleryItemImg src={item.src} alt={item.altText} />
                    )}
                </GalleryItem>
            ))}
        </Slider>
    );

    const thumbnailsCarousel = (
        <GalleryThumbnailsInRows>
            <Slider ref={sliderThumbs} {...settingsThumbs}>
                {mobileThumbnails?.map((item, i) => (
                    <AccessibilityDiv key={item.src}>
                        <GalleryThumbnail
                            id={item.src}
                            tabIndex={0}
                            $isActive={i === selectedId}
                            className={(i === selectedId && 'active') || ''}
                            onClick={() => handleThumbnailClick(i)}
                            onKeyDown={(e) => handleTabEnterPress(i, e)}
                            role="button"
                            onFocus={() => setIsThumbnailFocused(true)}
                            onBlur={() => setIsThumbnailFocused(false)}
                        >
                            <GalleryThumbnailImg src={item.src} alt={item.altText} />
                        </GalleryThumbnail>
                    </AccessibilityDiv>
                ))}
                {thumbnails.length > THUMBNAILS_PER_ROW.MOBILE && (
                    <ButtonAccessibilityDiv $isShowMore={hasShowMoreThumbnails}>
                        <ShowMoreThumbnailsButton
                            tabIndex={0}
                            onClick={onShowMoreThumbnailsClick}
                            $isShowMore={hasShowMoreThumbnails}
                        >
                            +{numberOfThumbnailsHidden}
                        </ShowMoreThumbnailsButton>
                    </ButtonAccessibilityDiv>
                )}
            </Slider>
        </GalleryThumbnailsInRows>
    );

    return (
        <GalleryWrapper>
            {isZoomEnabled && <SpotlightCursor />}
            {!isModalOpen && (
                <GalleryWrapper>
                    {galleriesCarousel}
                    {isFloatingNavButtonsEnabled && floatingNavButtons}
                </GalleryWrapper>
            )}

            {renderMarketingTag != null && renderMarketingTag()}
            {thumbnails.length > 1 ? thumbnailsCarousel : null}
        </GalleryWrapper>
    );
};
